/**
 * Minimal path-based (History API) router. Routes render async into #app.
 * Intercepts clicks on <a data-link> for SPA navigation.
 */

export type RouteParams = Record<string, string>;
export type RouteHandler = (root: HTMLElement, params: RouteParams) => void | Promise<void>;

interface Route {
  regex: RegExp;
  keys: string[];
  handler: RouteHandler;
}

const routes: Route[] = [];
let notFound: RouteHandler | null = null;
let rootEl: HTMLElement;

function compile(pattern: string): { regex: RegExp; keys: string[] } {
  const keys: string[] = [];
  const src = pattern
    .replace(/\/$/, '')
    .replace(/:[^/]+/g, (m) => {
      keys.push(m.slice(1));
      return '([^/]+)';
    });
  return { regex: new RegExp(`^${src || '/'}/?$`), keys };
}

export function route(pattern: string, handler: RouteHandler) {
  const { regex, keys } = compile(pattern);
  routes.push({ regex, keys, handler });
}

export function setNotFound(handler: RouteHandler) {
  notFound = handler;
}

export function navigate(path: string) {
  if (path === location.pathname + location.search) return render();
  history.pushState({}, '', path);
  render();
}

export async function render() {
  const path = location.pathname.replace(/\/+$/, '') || '/';
  for (const r of routes) {
    const m = r.regex.exec(path);
    if (m) {
      const params: RouteParams = {};
      r.keys.forEach((k, i) => (params[k] = decodeURIComponent(m[i + 1])));
      rootEl.scrollTop = 0;
      window.scrollTo(0, 0);
      await r.handler(rootEl, params);
      return;
    }
  }
  if (notFound) await notFound(rootEl, {});
}

export function startRouter(root: HTMLElement) {
  rootEl = root;
  window.addEventListener('popstate', () => void render());
  document.addEventListener('click', (e) => {
    const a = (e.target as HTMLElement).closest('a[data-link]') as HTMLAnchorElement | null;
    if (!a) return;
    const href = a.getAttribute('href');
    if (!href || href.startsWith('http')) return;
    e.preventDefault();
    navigate(href);
  });
  void render();
}
